import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { selectDemographicAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './DemographicAnalytics.module.css';
import { DemographicBarChart } from './DemographicBarChart/DemographicBarChart';
import { DemographicFilterSelect } from './DemographicFilterSelect/DemographicFilterSelect';
import { DemographicBarChartIcon } from './DemographicBarChart/DemographicBarChartIcon';
import {
  VoyagerKeyType,
  VOYAGER_FRIENDLY_NAMES,
} from '@mm/state/interfaces/voyagerLeads';
import { voyagerAttributesSortedSelector } from '@mm/state/selectors/voyagerLeadsSelectors';
import { uniqueId } from 'lodash';
import { useVoyagerCRUDActions } from '@mm/state/actions/VoyagerCRUDActions';
import { useCreateFilterParams } from '../../Contacts/use-create-filter-params';
import { MyLeadsFilterBar } from '../../Contacts/MyLeadsFilterBar/MyLeadsFilterBar';
import { useFilterParams } from '../../Contacts/MyLeadsFiltersPanel/use-filter-params';
import { DemographicAudienceSelect } from './DemographicFilterSelect/DemographicAudienceSelect';
import {
  DemographicAnalyticsCategory,
  DemographicAnalyticsKeyType,
} from '@mm/state/interfaces/botAnalytics';
import { convertToUpperSnakeCase } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { mapVoyagerAttributeItem } from './constants';
import classNames from 'classnames';
import { Spin } from 'antd';
import { MyLeadsFilterTypes } from '@mm/state/interfaces/myLeads';
import { VoyagerRemoteFiltersSearch } from '../../GetLeads/VoyagerLeads/VoyagerRemoteFiltersSearch/VoyagerRemoteFiltersSearch';

export const DemographicAnalytics = () => {
  const attributes = useSelector(voyagerAttributesSortedSelector);
  const botId = useSelector(activeChatbotIdSelector);
  const demographicAnalytics = useSelector(selectDemographicAnalytics);
  const individualAnalytics = Object.entries(
    demographicAnalytics?.individual || {},
  );
  const locationAnalytics = Object.entries(
    demographicAnalytics?.location || {},
  );

  const incomeAnalytics = Object.entries(demographicAnalytics?.income || {});

  const [busy, setBusy] = useState<DemographicAnalyticsCategory[] | undefined>(
    undefined,
  );
  const [fetchingFilters, setFetchingFilters] = useState<boolean>(false);
  const [selectedAudiencesIds, setSelectedAudiencesIds] = useState<
    number[] | undefined
  >(undefined);

  const { filterParams, setFilterParams } = useCreateFilterParams();

  const normalizeVoyagerKey = (key: string) => key.replace('VOY_', '');

  const data = [
    {
      id: 'individual',
      title: 'Individual',
      items: individualAnalytics,
    },
    {
      id: 'location',
      title: 'Location & Home',
      items: locationAnalytics,
    },
    {
      id: 'income',
      title: 'Income & Professional',
      items: incomeAnalytics,
    },
  ];

  const { retrieveDemographicAnalytics, resetAllBotAnalytics } =
    useBotAnalyticsCRUDActions();
  const { retrieveAttributes } = useVoyagerCRUDActions();
  const { getUpdatedFilterParams } = useFilterParams({
    filterParams,
  });

  const updateLocation = (key: VoyagerKeyType, value: string) => {
    const updatedFilters = getUpdatedFilterParams({
      key: `VOY_${key}`,
      value: value.toLowerCase(),
      type: MyLeadsFilterTypes.variables,
      isExisting: false,
      skipPrevValues: true,
    });
    setFilterParams(updatedFilters);
  };

  const fetchAnalytics = async (category: DemographicAnalyticsCategory) => {
    setBusy(state => [...(state || []), category]);
    await retrieveDemographicAnalytics(
      botId,
      category,
      filterParams.query,
      selectedAudiencesIds,
    );
    setBusy(state => state?.filter(c => c !== category));
  };

  useEffect(() => {
    (async () => {
      setFetchingFilters(true);
      await retrieveAttributes({ isExtended: true, withPrefix: true });
      setFetchingFilters(false);
    })();
  }, []);

  useEffect(() => {
    fetchAnalytics('individual');
    fetchAnalytics('location');
    fetchAnalytics('income');
    return () => {
      resetAllBotAnalytics();
    };
  }, [filterParams.query, selectedAudiencesIds]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>Demographic Data Analytics</div>
      <div className={styles.blockWrapper}>
        <div>Pick an audience</div>
        <DemographicAudienceSelect
          selectedAudiencesIds={selectedAudiencesIds}
          setSelectedAudiencesIds={setSelectedAudiencesIds}
          disabled={!!busy?.length}
        />
      </div>
      <div className={styles.blockWrapper}>
        {' '}
        <div>Filter results </div>
        <div className={styles.filters}>
          {attributes.map(attr => {
            const voyAttr = normalizeVoyagerKey(attr.attribute);

            return (
              <DemographicFilterSelect
                key={uniqueId(attr.attribute)}
                filterKey={attr.attribute}
                filterType={MyLeadsFilterTypes.variables}
                label={VOYAGER_FRIENDLY_NAMES?.[voyAttr]}
                options={attr.items.map(item =>
                  mapVoyagerAttributeItem(attr.attribute, item),
                )}
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                getUpdatedFilterParams={getUpdatedFilterParams}
                disabled={!!busy?.length}
                isLoading={fetchingFilters}
              />
            );
          })}
          <VoyagerRemoteFiltersSearch
            className={styles.demographicFilterSelect}
            placeholder="City, State, Zip"
            onSelect={updateLocation}
            lengthLimit={2}
            query={filterParams.query?.variables}
            isLocation
          />
        </div>
      </div>
      <MyLeadsFilterBar
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        closable={!busy?.length}
      />
      <Spin
        size="small"
        spinning={!!busy?.length}
        wrapperClassName={classNames(styles.spin, {
          [styles.busySpin]: busy,
        })}
      >
        <div className={styles.content}>
          {data.map(i => (
            <div className={styles.demographicGroup} key={i.title}>
              <div className={styles.groupTitle}>{i.title}</div>
              {i.items?.map(([keyName, data]) => {
                const key = normalizeVoyagerKey(
                  convertToUpperSnakeCase(keyName),
                );
                return (
                  <DemographicBarChart
                    key={keyName}
                    title={VOYAGER_FRIENDLY_NAMES[key]}
                    icon={
                      <DemographicBarChartIcon
                        name={key as DemographicAnalyticsKeyType}
                      />
                    }
                    data={data}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </Spin>
    </div>
  );
};
